import { unionNodeGhIds } from '../../../utils/computeGroupTree'

// Sdílené sestavení "řádků = stanice" pro skupinové tabulkové produkty (grp-mly / grp-yly /
// grp-jevy). Obdoba leafGhIdsOrdered z MeasDataGrid.jsx, jen orientovaná do řádků místo sloupců.
//
// Vrací pole řádků v pořadí daném stromem (sourozenci v pořadí ze stromu, stanice uvnitř listu
// abecedně podle názvu). Každý řádek:
//   { rowKey, gh, branch }  — branch = pole uzlů (od nejvyššího "nodes" po list), kterým řádek vznikl.
//
// Dedup se dělá jen v rámci jednoho nejvyššího uzlu z "nodes" — napříč sourozeneckými podskupinami
// se stejná stanice smí objevit vícekrát (patří-li do 2 podskupin, má být v obou), stejně jako
// v MeasDataGrid.
function collectLeavesWithBranch(node, branch) {
  const path = [...branch, node]
  if (!node.children || node.children.length === 0) return [{ node, path }]
  return node.children.flatMap(child => collectLeavesWithBranch(child, path))
}

export function buildStationRows(nodes, stationsByGhId) {
  const result = []
  for (const topNode of nodes) {
    const seen = new Set()
    for (const { node: leaf, path } of collectLeavesWithBranch(topNode, [])) {
      const ghIds = [...unionNodeGhIds(leaf)].sort((a, b) => {
        const na = stationsByGhId.get(a)?.name ?? a
        const nb = stationsByGhId.get(b)?.name ?? b
        return na.localeCompare(nb, 'cs')
      })
      for (const gh of ghIds) {
        if (seen.has(gh)) continue
        seen.add(gh)
        result.push({ rowKey: `${topNode.label}::${leaf.label}::${gh}`, gh, branch: path })
      }
    }
  }
  return result
}

// Pro daný sloupec hlavičky řádků (úroveň `depth` ve stromu) spočítá souvislé bloky řádků patřící
// témuž uzlu — vrací [{ node, label, start, span }]. Používá se k vykreslení víceúrovňové hlavičky
// vlevo přes rowSpan (obdoba colSpan v buildHeaderRows z MeasDataGrid, jen otočené).
export function rowHeaderBlocks(rows, depth) {
  const blocks = []
  let i = 0
  while (i < rows.length) {
    const node = rows[i].branch[depth] ?? null
    if (!node) { i++; continue }
    let span = 1
    while (i + span < rows.length && rows[i + span].branch[depth] === node) span++
    blocks.push({ node, label: node.label, start: i, span })
    i += span
  }
  return blocks
}

// Set indexů řádků, kde na úrovni `level` (0 = nejvyšší = "nodes") začíná nová skupina —
// tj. kde se branch[level] liší od předchozího řádku. Používá se k protažení silné vodorovné
// oddělovací čáry mezi skupinami napříč celým řádkem (obdoba levelStarts z MeasDataGrid).
export function rowGroupStarts(rows, level = 0) {
  const starts = new Set()
  for (let i = 0; i < rows.length; i++) {
    if (i === 0) continue
    if (rows[i].branch[level] !== rows[i - 1].branch[level]) starts.add(i)
  }
  return starts
}

// Max. hloubka hierarchie napříč řádky (kolik sloupců hlavičky vlevo je potřeba). 1 = jen "nodes"
// samotné (přímé děti vybraného uzlu / root uzly). Klik na list = nodes=[1 list bez dětí] → 0
// (žádná hierarchie nad stanicemi, jen prostý seznam).
export function rowHierarchyDepth(nodes) {
  if (nodes.length === 1 && (!nodes[0].children || nodes[0].children.length === 0)) return 0
  let max = 0
  const walk = (node, d) => {
    max = Math.max(max, d)
    if (node.children) for (const c of node.children) walk(c, d + 1)
  }
  for (const n of nodes) walk(n, 1)
  return max
}
